<template>
    <div>
        <div
            v-bind="getRootProps()"
            class="p-image-dropzone"
            :class="{ 'small': small, 'has-image': previewUrl }"
            ref="dropzoneRoot"
        >
            <input v-bind="getInputProps()" />
            <div v-if="previewUrl" class="preview-container">
                <img :src="previewUrl" class="preview-image" @click.stop="open" />
                <button type="button" class="remove-btn" @click.stop="removeImage">
                    <i class="bi bi-x-circle-fill text-danger"></i>
                </button>
            </div>
            <div v-else class="dropzone-message">
                <em class="fas fa-upload text-muted"></em><br>
                Drop image to upload
            </div>
        </div>
    </div>
</template>

<script>
import { useDropzone } from 'vue3-dropzone';
import baseInputs from '@/components/mixins/BaseInputs';

export default {
    name: 'p-image',
    mixins: [baseInputs],
    props: {
        small: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        value() { return this.modelValue; },
    },
    setup() {
        let onDropCallback = null;

        function onDrop(acceptedFiles) {
            if (onDropCallback && acceptedFiles.length > 0) {
                onDropCallback(acceptedFiles[0]);
            }
        }

        const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
            onDrop,
            accept: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'],
            multiple: false
        });

        return { getRootProps, getInputProps, open, isDragActive, setOnDrop: (fn) => { onDropCallback = fn; } };
    },
    data() {
        return {
            previewUrl: null,
            isUnmounting: false,
            initialized: false
        };
    },
    created() {
        this.setOnDrop(this.handleFileDrop);
    },
    mounted() {
        if (this.value?.dataURL) {
            this.previewUrl = decodeURIComponent(this.value.dataURL);
        }
        this.initialized = true;
    },
    watch: {
        modelValue: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                if (newVal === oldVal) return;
                if (newVal?.dataURL) {
                    this.previewUrl = decodeURIComponent(newVal.dataURL);
                } else if (!newVal) {
                    this.previewUrl = null;
                }
            }
        }
    },
    methods: {
        handleFileDrop(file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                // Merge into existing value object to preserve structure for the server
                const imageData = {
                    ...(this.value || {}),
                    dataURL: event.target.result,
                    index: this.value?.index ?? 0,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    upload: { filename: file.name }
                };
                this.previewUrl = event.target.result;
                this.$emit('newURL', event.target.result);
                this.handleInput(imageData);
            };
            reader.readAsDataURL(file);
        },
        removeImage() {
            if (!this.isUnmounting) {
                this.previewUrl = null;
                this.$emit('newURL', null);
                // Preserve the index/structure so the server can match the slot
                // and clear the file in place. Emitting null would drop the index.
                this.handleInput({
                    ...(this.value || {}),
                    dataURL: null,
                    upload: null,
                    name: null,
                    size: 0,
                    type: null
                });
            }
        }
    },
    beforeUnmount() {
        this.isUnmounting = true;
    }
};
</script>

<style scoped lang="scss">
@import '@/styles/app/common/variables.scss';

.p-image-dropzone {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 277px;
    min-height: 200px;
    border: 2px dashed $input-default-border-color;
    border-radius: 4px;
    cursor: pointer;
    position: relative;
    background: #fafafa;
    transition: border-color 0.2s;

    &:hover {
        border-color: $primary;
    }

    &.has-image {
        border-style: solid;
        border-width: 1px;
        min-height: auto;
    }
}

.dropzone-message {
    font-size: 16px;
    text-align: center;
    padding: 30px;
    color: $text-muted;
}

.preview-container {
    position: relative;
    padding: 0.5rem;
}

.preview-image {
    max-width: 220px;
    max-height: 220px;
    object-fit: contain;
    display: block;
    cursor: pointer;
}

.remove-btn {
    position: absolute;
    top: 0;
    left: 2px;
    background: radial-gradient(white 40%, transparent 40%);
    border: none;
    cursor: pointer;
    font-size: 1.25rem;
    padding: 0;
}

.small {
    width: 89px;
    min-height: 86px;

    .dropzone-message {
        font-size: 10px;
        min-height: 86px;
        padding: 15px;
    }

    .preview-image {
        max-width: 85px;
        max-height: 85px;
    }
}
</style>
